import { bind, classNames, Component, h, signal, tag } from "omi";

import { NWCloseButton } from "@/components/atoms/nw-button/closeButton";
import { NWToggle } from "@/components/atoms/nw-toggle";
import { networkSignal } from "@/utils/signals";
import { getReturnTo } from "@/utils/url";
import { ModalProps, NWError, openModal } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { BillingPeriod, ProductPrice, ProductPriceName, ProductPrices } from "./models";
import { PricingSection } from "./pricingSection";

export type PaywallModalData = {};

type Props = ModalProps<PaywallModalData>;

@tag("paywall-modal")
export class PaywallModal extends Component<Props> {
  static css = [tailwind];
  private name = signal<string | undefined>(undefined);
  private checkoutUrl = networkSignal<string>();
  private billingPeriod = signal<BillingPeriod>(BillingPeriod.MONTHLY);

  installed() {
    this.name.value = Store.user.getUsersName() || "fellow nextWorker";
  }

  @bind
  async handleOnClick(name: ProductPriceName) {
    if (!Store.features.hasFeature("projects.payments")) {
      return;
    }

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackPaywallButtonClicked(name);
    });

    if (!Store.user.isLoggedIn.value) {
      this.handleAnonymousClick();
      return;
    }

    const returnUrl = getReturnTo();
    await this.checkoutUrl.run(() => this.getCheckoutUrl(returnUrl));

    const checkoutUrl = this.checkoutUrl.signal.data.value;
    if (!checkoutUrl) {
      return;
    }

    window.location.replace(checkoutUrl);
  }

  @bind
  private handleAnonymousClick() {
    this.props.close();
    setTimeout(() => {
      openModal("login", { returnTo: getReturnTo() });
    }, 500);
  }

  @bind
  private handleFreeProductClick() {
    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackPaywallButtonClicked(ProductPriceName.FREE);
    });

    if (!Store.user.isLoggedIn.value) {
      this.handleAnonymousClick();
      return;
    }

    this.props.close();
  }

  private async getCheckoutUrl(returnUrl: string) {
    const res = await fetch(`/checkout/create?returnTo=${returnUrl}&billing_period=${this.billingPeriod.value}`);
    const json = await res.json();
    if (res.ok) {
      return json.checkoutUrl;
    }

    throw new Error("Failed to get checkout URL");
  }

  @bind
  private handleBillingPeriodChange(checked: boolean) {
    this.billingPeriod.value = checked ? BillingPeriod.ANNUAL : BillingPeriod.MONTHLY;

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackPaywallBillingPeriodToggled(this.billingPeriod.value);
    });
  }

  render(props: Props) {
    const hasError = !!this.checkoutUrl.signal.error.value;

    return (
      <div className="flex justify-center items-center sm:mt-0">
        <div class="bg-white shadow-lg border p-6 sm:p-10 border-gray-200 max-w-screen-xl rounded-[32px] w-fit max-h-[90vh] sm:max-h-3/4 overflow-y-auto z-[100] sm:mx-4 min-w-[340px]">
          <div class="header flex flex-col-reverse sm:flex-row justify-between items-end sm:items-center sm:mb-16 space-y-4 sm:space-y-0">
            <div class="w-11 h-11"></div>
            <div className="bg-graysecondary-50 px-6 py-4 flex col-span-2 w-fit mx-auto items-center gap-6 rounded-lg">
              <p className="text-tertiary">Save with an annual plan</p>
              <NWToggle
                checked={this.billingPeriod.value === BillingPeriod.ANNUAL}
                onChange={this.handleBillingPeriodChange}
              />
            </div>
            <div class="flex justify-end">
              <NWCloseButton size="lg" onClick={props.close}></NWCloseButton>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 space-y-8 mb-6 sm:mb-0 sm:space-y-0">
            {ProductPrices.map((price: ProductPrice, idx) => (
              <div
                className={classNames("max-w-1/2 w-full h-full flex-1", {
                  "!first:pt-0": idx === 0,
                })}
              >
                <PricingSection
                  {...price}
                  billingPeriod={this.billingPeriod.value}
                  onClick={
                    price.name === ProductPriceName.FREE
                      ? this.handleFreeProductClick
                      : () => this.handleOnClick(price.name)
                  }
                  recommended={price.name === ProductPriceName.PRO}
                  showPriceSubtext={price.name !== ProductPriceName.FREE}
                  isLoading={price.name === ProductPriceName.PRO ? this.checkoutUrl.signal.isLoading.value : undefined}
                />
              </div>
            ))}
          </div>
          {hasError && (
            <div className="mt-4">
              <NWError />
            </div>
          )}
        </div>
      </div>
    );
  }
}
